import {
  ItemDto,
  OrderDto,
  OrderLineDto,
  OrderPhysicianDto
} from '../../types/swaggerTypes'
import { PatientOrderStatus } from '../Patients/PatientDashboard/Patient.model'
import { resolveRememberedAddressId } from '../../utils/lastUsedAddressService'

/** Medication line used for MA checkout header / defaults. */
export const getMedicationOrderLine = (
  order: OrderDto | undefined
): OrderLineDto | undefined =>
  order?.lines?.find((line) => line.medicationFlag === 'Y')

/**
 * Line to fall back on for patient / programme fields: the medication line,
 * else any line carrying a patient number. Data Platform draft orders (before
 * Place order) can leave every line unflagged with null patient fields.
 */
export const getPatientOrderLine = (
  order: OrderDto | undefined
): OrderLineDto | undefined =>
  getMedicationOrderLine(order) ??
  order?.lines?.find((line) => !!line.patientNumber)

/** Format physician title + full name for OpaHeader display. */
export const formatPhysicianDisplayName = (
  physician: OrderPhysicianDto | null | undefined
): string | undefined => {
  if (!physician?.physicianFullName) {
    return undefined
  }
  if (physician.physicianTitle) {
    return `${physician.physicianTitle} ${physician.physicianFullName}`
  }
  return physician.physicianFullName
}

export interface OpaCheckoutLocationState {
  patientNumber?: string
  physicianId?: string | number
}

/**
 * Resolve Patient ID and Physician header fields for MA checkout.
 * Prefers router location state (every entry into checkout passes it); falls
 * back to the order line, which is incomplete on Data Platform draft orders.
 * The physician name still comes from the order line when it has one.
 * Display values must not depend on getPatientDefaultDetail succeeding.
 */
export const resolveOpaCheckoutHeaderFields = (
  orderItem: OrderLineDto | undefined,
  locationState?: OpaCheckoutLocationState
): {
  patientNumber?: string
  physicianId?: string
  physicianDisplayName?: string
} => {
  const patientNumber =
    locationState?.patientNumber || orderItem?.patientNumber || undefined

  const physicianIdFromOrder = orderItem?.physician?.physicianId
  const physicianIdFromState = locationState?.physicianId
  const physicianId =
    physicianIdFromState != null && String(physicianIdFromState) !== ''
      ? String(physicianIdFromState)
      : physicianIdFromOrder != null
      ? String(physicianIdFromOrder)
      : undefined

  const physicianDisplayName = formatPhysicianDisplayName(orderItem?.physician)

  return {
    patientNumber: patientNumber || undefined,
    physicianId,
    physicianDisplayName
  }
}

/**
 * CLOS-14060 — PTA checkout header shows Clinical Trial ID instead of DOB.
 */
export const resolveOpaCheckoutPatientSecondaryAttribute = ({
  isPostTrialAccess,
  dateOfBirth,
  clinicalTrialId
}: {
  isPostTrialAccess: boolean
  dateOfBirth?: string
  clinicalTrialId?: string | null
}): { dateOfBirth?: string; clinicalTrialId?: string } => {
  if (isPostTrialAccess) {
    const trimmed = clinicalTrialId?.trim()
    return {
      clinicalTrialId: trimmed || undefined
    }
  }

  return { dateOfBirth }
}

export enum OrderStatus {
  Processing = 'Processing', // order is booked
  Submitted = 'Submitted', // order is not booked
  OnHold = 'On Hold',
  Cancelled = 'Cancelled',
  Shipped = 'Shipped',
  Reassigned = 'Reassigned'
}

/** Orders that have left checkout must never be submitted again. */
export const isTerminalOpaCheckoutOrder = (order: OrderDto): boolean =>
  order.orderStatus === OrderStatus.Shipped ||
  order.orderStatus === OrderStatus.Cancelled

const normaliseOrderStatus = (status?: string): string =>
  (status ?? '').replace(/_/g, ' ').trim().toLowerCase()

/**
 * Statuses an order can only reach after Place order: it is booked, in review,
 * on hold, moving or closed. A draft that can still be resumed reads Submitted
 * (or Entered), so those are deliberately absent.
 */
const BOOKED_ORDER_STATUSES = new Set([
  'on hold',
  'processing',
  'awaiting shipping',
  'under medical review',
  'under review',
  'health authority review',
  'medical review deferred',
  'medical review declined',
  'health authority declined',
  'in transit',
  'out for delivery',
  'failed attempt',
  'available for pickup',
  'delivered',
  'failed delivery',
  'shipped',
  'cancelled'
])

/**
 * An order that has already been placed. Checkout must not open it again, or
 * Place order would re-book it (CLOS-14645). Only for reading an order when
 * checkout opens: the response of Place order itself is a booked order, so it
 * must not be checked with this.
 */
export const isBookedOpaCheckoutOrder = (order: OrderDto): boolean =>
  BOOKED_ORDER_STATUSES.has(normaliseOrderStatus(order.orderStatus))

export enum OrderItemType {
  DataLoggers = 'Data Loggers', // Data Loggers - Charge
  NOIAndCDLicenseItems = 'NOI and CD License Items', // NOI and CD License Items – N/A (won’t ever appear on order) - Charge
  Shippers = 'Shippers', // Shippers - Charge
  ProductionComponents = 'Production Components', // Production Components– N/A (won’t ever appear on order) - Charge
  ServiceItems = 'Service Items', // Service Items - Charge
  Equipment = 'Equipment', // Equipment – Charge
  AncillaryEquipment = 'Ancillary Equipment', // Ancillary Equipment - Ancillaries
  ProductionLabels = 'Production Labels', // Production Labels – N/A (won’t ever show) - Charge
  Pharmaceuticals = 'Pharmaceuticals', // Pharmaceuticals - Product
  PostProduction = 'Post Production', // Post Production - Product
  RawMaterials = 'Raw Materials' // Raw Materials - Product
}

export enum LocalStorageOPAKeys {
  DeliveryNote = 'deliveryNote'
}

export const isProductOrderItem = (orderLine: OrderLineDto) => {
  const itemCategoryCode = orderLine.skuCatalogItem?.item.itemCategoryCode
  return (
    itemCategoryCode === OrderItemType.Pharmaceuticals ||
    itemCategoryCode === OrderItemType.PostProduction ||
    itemCategoryCode === OrderItemType.RawMaterials
  )
}

// Extract the comparison logic to reduce cognitive complexity
export const hasAncillaryItemCategory = (orderLine: OrderLineDto): boolean =>
  orderLine.skuCatalogItem?.item.itemCategoryCode ===
  OrderItemType.AncillaryEquipment

export const isAncillaryOrderItem = (orderLine: OrderLineDto) =>
  hasAncillaryItemCategory(orderLine)

export const isProductTemperatureTracker = (item: ItemDto | undefined) =>
  item &&
  item.division === 'CTL' &&
  item.itemCategoryCode === OrderItemType.DataLoggers

const nonChargeItemCatCodes = [
  OrderItemType.AncillaryEquipment.toString(),
  OrderItemType.Pharmaceuticals.toString(),
  OrderItemType.PostProduction.toString(),
  OrderItemType.RawMaterials.toString()
]

export const isChargeLineItem = (item: ItemDto | undefined) => {
  return (
    item &&
    item.itemCategoryCode &&
    nonChargeItemCatCodes.indexOf(item.itemCategoryCode) === -1
  )
}

/**
 * Sum up charges from line items
 * @param order
 */
export const getTotalCharges = (order: OrderDto) => {
  let sum = 0
  order.lines.forEach((line) => {
    const item: ItemDto | undefined = line.skuCatalogItem?.item
    if (isChargeLineItem(item)) {
      sum += line.totals.subTotal
    }
  })
  return sum
}

export const isOrderCancellable = (order: OrderDto) =>
  order?.lines.reduce((previousValue, currentValue): boolean => {
    return currentValue.status === OrderStatus.Cancelled ||
      currentValue.status === OrderStatus.Shipped
      ? false
      : previousValue
  }, true)

export const getOrderItems = (order: OrderDto | undefined) => {
  const orderedItems: OrderLineDto[] = []
  order?.lines.forEach((orderLine) => {
    if (
      (isProductOrderItem(orderLine) || isAncillaryOrderItem(orderLine)) &&
      (orderLine.status === OrderStatus.Cancelled ||
        orderLine.quantities.ordered > 0 ||
        orderLine.status === PatientOrderStatus.MedicalReviewDeclined ||
        orderLine.status === PatientOrderStatus.HealthAuthorityDeclined)
    ) {
      orderedItems.push(orderLine)
    }
  })
  return orderedItems
}

export const getReassignedOrderItems = (order: OrderDto | undefined) => {
  const reasignedItems: OrderLineDto[] = []
  order?.lines.forEach((orderLine) => {
    if (
      (isProductOrderItem(orderLine) || isAncillaryOrderItem(orderLine)) &&
      ((orderLine.vialsReassignedQuantity &&
        orderLine.vialsReassignedQuantity > 0) ||
        orderLine.status === PatientOrderStatus.MedicalReviewDeclined ||
        orderLine.status === PatientOrderStatus.HealthAuthorityDeclined)
    ) {
      reasignedItems.push(orderLine)
    }
  })
  return reasignedItems
}

export const getOrderAndReassignedItems = (order: OrderDto | undefined) => {
  const orderedItems: OrderLineDto[] = []
  order?.lines.forEach((orderLine) => {
    if (
      (isProductOrderItem(orderLine) || isAncillaryOrderItem(orderLine)) &&
      (orderLine.status === OrderStatus.Cancelled ||
        orderLine.quantities.ordered > 0 ||
        orderLine.status === PatientOrderStatus.MedicalReviewDeclined ||
        orderLine.status === PatientOrderStatus.HealthAuthorityDeclined ||
        (orderLine.vialsReassignedQuantity &&
          orderLine.vialsReassignedQuantity > 0)) &&
      !isProductTemperatureTracker(orderLine.skuCatalogItem?.item)
    ) {
      orderedItems.push(orderLine)
    }
  })

  return orderedItems
}

const canBeParsedAsNumber = (value: string) => {
  return !Number.isNaN(Number.parseInt(value))
}

export const getAdditionalLineDescription = (orderLine: OrderLineDto) => {
  if (
    orderLine &&
    orderLine.skuCatalogItem &&
    orderLine.skuCatalogItem.item &&
    orderLine.skuCatalogItem.item.itemCategoryCode
  ) {
    switch (orderLine.skuCatalogItem?.item.itemCategoryCode) {
      case OrderItemType.AncillaryEquipment:
        return 'opa_checkout:additional_lines.ancillary_equipment'
      case OrderItemType.DataLoggers:
        if (orderLine.skuCatalogItem.item.storageTemperature === '15-25') {
          return 'opa_checkout:additional_lines.data_loger_ambient'
        } else if (
          orderLine.skuCatalogItem.item.storageTemperature === '2-8' ||
          (orderLine.skuCatalogItem.item.storageTemperature &&
            canBeParsedAsNumber(
              orderLine.skuCatalogItem.item.storageTemperature
            ) &&
            (orderLine.skuCatalogItem.item
              .storageTemperature as unknown as number) < 0)
        )
          return 'opa_checkout:additional_lines.data_logger_cold'
        else {
          return 'opa_checkout:additional_lines.data_logger_default'
        }
      case OrderItemType.Shippers:
        if (orderLine.skuCatalogItem.item.storageTemperature === '15-25') {
          return 'opa_checkout:additional_lines.shippers_ambient'
        } else if (orderLine.skuCatalogItem.item.storageTemperature === '2-8')
          return 'opa_checkout:additional_lines.shippers_cool'
        else {
          return 'opa_checkout:additional_lines.shippers_default'
        }
      case OrderItemType.ServiceItems:
        return 'opa_checkout:additional_lines.service_items'
      case OrderItemType.ProductionComponents:
        return 'opa_checkout:additional_lines.production_components'
      case OrderItemType.Equipment:
        return 'opa_checkout:additional_lines.equipment_items'
      case OrderItemType.NOIAndCDLicenseItems:
        return 'opa_checkout:additional_lines.no_iand_cd_license_items'
      case OrderItemType.ProductionLabels:
        return 'opa_checkout:additional_lines.production_label_items'

      default:
        return ''
    }
  } else {
    return ''
  }
}

export const deliveryNoteMaxCharacters = 240

/**
 * Address ids the checkout may submit: the institute's own ship-to list, plus any
 * address created during this checkout. A created address is added to the offered
 * list and selected immediately, without the institute being refetched, so leaving
 * it out here would reject the user's own new address.
 */
export const resolveSelectableAddressIds = (
  instituteAddresses: ReadonlyArray<{ addressId: string }> | undefined,
  offeredAddresses: ReadonlyArray<{ addressId: string }> | undefined
): string[] => [
  ...(instituteAddresses ?? []).map((address) => address.addressId),
  ...(offeredAddresses ?? []).map((address) => address.addressId)
]

/**
 * Ship-to id to submit for an MA order.
 *
 * The selected id is only used when the current institute actually offers it,
 * so an address remembered at another institute cannot be submitted, and the
 * institute's primary address is the fallback. Returns null when neither can be
 * confirmed, including while the institute's addresses are still loading. The
 * caller must not submit in that case: an unchecked id could belong to another
 * institute, and a null ship-to is rejected downstream.
 */
export const resolveShipToOrgId = (
  selectedAddressId: string | number | null | undefined,
  instituteAddressIds: ReadonlyArray<string | number | null | undefined>,
  institutePrimaryAddressId: string | number | null | undefined
): number | null => {
  const candidate =
    resolveRememberedAddressId(selectedAddressId, instituteAddressIds) ??
    institutePrimaryAddressId

  const parsed = Number.parseInt(String(candidate ?? ''), 10)
  return Number.isNaN(parsed) ? null : parsed
}
